import {
  addToLocalStorage,
  APARTMENT_KEY,
  AREA_KEY,
  COSTUNIT_KEY,
  foundInLocalStorage,
  getLocalStorage,
  isValidKey,
  removeFromLocalStorage,
  setLocalStorage,
} from "./hoasFavouritesHelpers";

/**
 * Checks if apartment is found from localstorage
 * @param {string} costUnitId Check if costUnitId matches
 * @param {string} apartmentType Check if apartment type matches
 * @param {string} apartmentRent Check if rent matches
 * @param {string} apartmentSurface Check if surface-area matches
 * @returns {bool}
 */
export const hasApartment = (
  costUnitId,
  apartmentType,
  apartmentRent,
  apartmentSurface,
) => {
  const apartment = getApartment(
    costUnitId,
    apartmentType,
    apartmentRent,
    apartmentSurface,
  );
  return apartment ? true : false;
};

/**
 * Get apartment object from localstorage
 * @param {string} costUnitId Check if costUnitId matches
 * @param {string} apartmentType Check if apartment type matches
 * @param {string} apartmentRent Check if rent matches
 * @param {string} apartmentSurface Check if surface-area matches
 * @returns {object | null}
 */
export const getApartment = (
  costUnitId,
  apartmentType,
  apartmentRent,
  apartmentSurface,
) => {
  if (!costUnitId) {
    console.error(
      "Missing costUnitId to check from localstorage: getApartment",
      costUnitId,
    );
    return null;
  }

  if (!apartmentType) {
    console.error(
      "Missing apartmentType to check from localstorage: getApartment",
      apartmentType,
    );
    return null;
  }

  if (!apartmentRent && apartmentRent !== 0) {
    console.error(
      "Missing apartmentRent to check from localstorage: getApartment",
      apartmentRent,
    );
    return null;
  }

  if (!apartmentSurface) {
    console.error(
      "Missing apartmentSurface to check from localstorage: getApartment",
      apartmentSurface,
    );
    return null;
  }

  const localStorageApartments = getLocalStorage(APARTMENT_KEY);

  if (!localStorageApartments || localStorageApartments.length <= 0) {
    return null;
  }

  let item = null;

  localStorageApartments.forEach((localStorageItem) => {
    if (
      parseInt(localStorageItem?.cost_unit_id) === parseInt(costUnitId) &&
      localStorageItem?.apartment_type.toLowerCase() ===
        apartmentType.toLowerCase() &&
      localStorageItem?.rent === apartmentRent &&
      localStorageItem?.surface_area === apartmentSurface
    ) {
      item = localStorageItem;
    }
  });

  return item;
};

/**
 * Remove apartment from localstorage
 * @param {string} costUnitId Check if costUnitId matches
 * @param {string} apartmentType Check if apartment type matches
 * @param {string} apartmentRent Check if rent matches
 * @param {string} apartmentSurface Check if surface-area matches
 * @returns {void}
 */
export const removeApartment = (
  costUnitId,
  apartmentType,
  apartmentRent,
  apartmentSurface,
) => {
  if (!costUnitId) {
    console.error(
      "Missing costUnitId to check from localstorage: removeApartment",
      costUnitId,
    );
    return null;
  }

  if (!apartmentType) {
    console.error(
      "Missing apartmentType to check from localstorage: removeApartment",
      apartmentType,
    );
    return null;
  }

  if (!apartmentRent && apartmentRent !== 0) {
    console.error(
      "Missing apartmentRent to check from localstorage: removeApartment",
      apartmentRent,
    );
    return null;
  }

  if (!apartmentSurface) {
    console.error(
      "Missing apartmentSurface to check from localstorage: removeApartment",
      apartmentSurface,
    );
    return null;
  }

  const localStorageApartments = getLocalStorage(APARTMENT_KEY);

  if (!localStorageApartments || localStorageApartments.length <= 0) {
    return;
  }

  const newItems = [];

  localStorageApartments.forEach((localStorageItem) => {
    if (
      parseInt(localStorageItem?.cost_unit_id ?? 0) === parseInt(costUnitId) &&
      localStorageItem?.apartment_type.toLowerCase() ===
        apartmentType.toLowerCase() &&
      localStorageItem?.rent === apartmentRent &&
      localStorageItem?.surface_area === apartmentSurface
    ) {
      return;
    }
    newItems.push(localStorageItem);
  });

  setLocalStorage(APARTMENT_KEY, newItems);
};

/**
 * Add apartment to localstorage
 * @param {object} apartmentObject
 * @returns {void}
 */
export const addApartment = (apartmentObject) => {
  if (!apartmentObject) {
    console.error("Missing apartmentObject in: addApartment", apartmentObject);
    return;
  }
  removeApartment(
    apartmentObject?.cost_unit_id,
    apartmentObject?.apartment_type,
    apartmentObject?.rent,
    apartmentObject?.surface_area,
  );
  addToLocalStorage(apartmentObject, APARTMENT_KEY);
};

/**
 * Checks if costUnitId is found from costUnits localstorage
 * @param {string} costUnitId
 * @returns {bool}
 */
export const hasCostUnit = (costUnitId) => {
  if (!costUnitId) {
    console.error(
      "Missing costUnitId to check from localstorage: hasCostUnit",
      costUnitId,
    );
    return;
  }
  return foundInLocalStorage(COSTUNIT_KEY, costUnitId, "id") ? true : false;
};

/**
 * Get cost-unit from cost-units localstorage
 * @param {string} costUnitId
 * @returns {object | null}
 */
export const getCostUnit = (costUnitId) => {
  if (!costUnitId) {
    console.error(
      "Missing costUnitId to check from localstorage: getCostUnit",
      costUnitId,
    );
    return null;
  }
  return foundInLocalStorage(COSTUNIT_KEY, costUnitId, "id");
};

/**
 * Remove cost-unit from localstorage
 * @param {string} costUnitId Check if costUnitId matches
 * @returns {void}
 */
export const removeCostUnit = (costUnitId) => {
  if (!costUnitId) {
    console.error("Missing costUnitId in: removeCostUnit", costUnitId);
    return;
  }
  if (hasCostUnit(costUnitId)) {
    removeFromLocalStorage(COSTUNIT_KEY, costUnitId, "id");
  }
};

/**
 * Add cost-unit to localstorage
 * @param {object} costUnitObject
 * @returns {void}
 */
export const addCostUnit = (costUnitObject) => {
  if (!costUnitObject) {
    console.error("Missing costUnitObject in: addCostUnit", costUnitObject);
    return;
  }
  removeCostUnit(costUnitObject?.id);
  addToLocalStorage(costUnitObject, COSTUNIT_KEY);
};

/**
 * Return boolean value --> found area id in localstorage areas
 * @param {string} areaId
 * @returns {bool}
 */
export const hasArea = (areaId) => {
  if (!areaId) {
    console.error("Missing areaId in localstorage check: hasArea", areaId);
    return false;
  }
  return foundInLocalStorage(AREA_KEY, areaId, "id") ? true : false;
};

/**
 * Get cost-unit from cost-units localstorage
 * @param {string} areaId
 * @returns {object | null}
 */
export const getArea = (areaId) => {
  if (!areaId) {
    console.error("Missing areaId to check from localstorage: getArea", areaId);
    return;
  }
  return foundInLocalStorage(AREA_KEY, areaId, "id");
};

/**
 * Remove area from localstorage
 * @param {string} areaId Check if costUnitId matches
 * @returns {void}
 */
export const removeArea = (areaId) => {
  if (!areaId) {
    console.error("Missing areaId in: removeArea", areaId);
    return;
  }
  if (hasArea(areaId)) {
    removeFromLocalStorage(AREA_KEY, areaId, "id");
  }
};

/**
 * Add area to localstorage
 * @param {object} areaObject
 * @returns {void}
 */
export const addArea = (areaObject) => {
  if (!areaObject) {
    console.error("Missing areaObject in: addArea", areaObject);
    return;
  }
  removeArea(areaObject?.id);
  addToLocalStorage(areaObject, AREA_KEY);
};

/**
 * Get by localstorage key
 * @param {*} key
 * @returns {any}
 */
export const getDataByKey = (localStorageKey) => {
  if (!isValidKey(localStorageKey)) {
    console.error("Not valid key in: getDataByKey", localStorageKey);
    return null;
  }
  switch (localStorageKey) {
    case COSTUNIT_KEY:
      return getLocalStorage(COSTUNIT_KEY);
    case APARTMENT_KEY:
      return getLocalStorage(APARTMENT_KEY);
    case AREA_KEY:
      return getLocalStorage(AREA_KEY);
    default:
      return null;
  }
};

/**
 * Check if has data by localstorage key
 * @param {string} key
 * @param {any} data
 * @returns {any}
 */
export const hasDataByKey = (localStorageKey, data) => {
  if (!isValidKey(localStorageKey)) {
    console.error("Not valid key in: hasDataByKey", localStorageKey);
    return null;
  }
  switch (localStorageKey) {
    case COSTUNIT_KEY: {
      return hasCostUnit(data?.id);
    }
    case APARTMENT_KEY: {
      const parentCostUnitId = data?.cost_unit_id;
      const apartmentType = data?.apartment_type;
      const rent = data?.rent;
      const surfaceArea = data?.surface_area;

      return hasApartment(parentCostUnitId, apartmentType, rent, surfaceArea);
    }
    case AREA_KEY: {
      return hasArea(data?.id);
    }
    default:
      return null;
  }
};

/**
 * Add data by localstorage key
 * @param {*} key
 * @returns {any}
 */
export const addDataByKey = (localStorageKey, data) => {
  if (!isValidKey(localStorageKey)) {
    console.error("Not valid key in: addDataByKey", localStorageKey);
    return null;
  }

  switch (localStorageKey) {
    case COSTUNIT_KEY: {
      return addCostUnit(data);
    }
    case APARTMENT_KEY: {
      return addApartment(data);
    }
    case AREA_KEY: {
      return addArea(data);
    }
    default:
      return null;
  }
};

/**
 * Remove data from localstorage by localstorage key and from favourite-object
 * @param {*} localStorageKey
 * @param {*} favouriteObject
 * @returns
 */
export const removeDataByKey = (localStorageKey, favouriteObject) => {
  if (!isValidKey(localStorageKey)) {
    console.error("Not valid key in: removeDataByKey", localStorageKey);
    return null;
  }

  switch (localStorageKey) {
    case COSTUNIT_KEY: {
      const costUnitId = favouriteObject?.id;
      return removeCostUnit(costUnitId);
    }

    case APARTMENT_KEY: {
      const parentCostUnitId = favouriteObject?.cost_unit_id;
      const apartmentType = favouriteObject?.apartment_type;
      const rent = favouriteObject?.rent;
      const surfaceArea = favouriteObject?.surface_area;

      return removeApartment(
        parentCostUnitId,
        apartmentType,
        rent,
        surfaceArea,
      );
    }

    case AREA_KEY: {
      const areaId = favouriteObject?.id;
      return removeArea(areaId);
    }

    default: {
      return null;
    }
  }
};

/**
 * Get all cost-units from localstorage
 * @returns {any}
 */
export const getCostUnits = () => {
  return getDataByKey(COSTUNIT_KEY) ?? [];
};

/**
 * Get apartments from localstorage
 * @returns {any}
 */
export const getApartments = () => {
  return getDataByKey(APARTMENT_KEY) ?? [];
};

/**
 * Get areas from localstorage
 * @returns {any}
 */
export const getAreas = () => {
  return getDataByKey(AREA_KEY) ?? [];
};
